import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../../Firebase';
import { ShieldCheck, Mail, Lock, AlertCircle, Loader } from 'lucide-react';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 1. Check if admins collection has any records. If not, seed a default admin account
      const adminsRef = collection(db, 'admins');
      const allAdminsSnap = await getDocs(adminsRef);
      
      if (allAdminsSnap.empty) {
        await setDoc(doc(db, 'admins', 'default_admin'), {
          email: 'admin@sahyadri.com',
          password: 'admin123',
          name: 'General Administrator',
          role: 'Super Admin'
        });
      }

      // 2. Query Firestore for the matching email and password
      const q = query(
        collection(db, 'admins'),
        where('email', '==', email.trim().toLowerCase()),
        where('password', '==', password)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const adminData = querySnapshot.docs[0].data();
        // Store session locally
        localStorage.setItem(
          'adminUser',
          JSON.stringify({
            email: adminData.email,
            name: adminData.name || 'Admin',
            role: adminData.role || 'Admin',
            isLoggedIn: true
          })
        );
        // Navigate to Dashboard
        navigate('/admin/dashboard');
      } else {
        setError('Invalid admin credentials. (Try admin@sahyadri.com / admin123)');
      }
    } catch (err) {
      console.error('Firebase authentication query failed:', err);
      setError('Connection failed. Please check internet access or Firestore rules.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020C1A] flex flex-col justify-center items-center p-4 relative overflow-hidden font-sans">
      {/* Background radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(179,24,37,0.12),transparent_65%)] pointer-events-none" />

      {/* Decorative floating grids */}
      <div className="absolute top-10 left-10 w-48 h-48 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-64 h-64 bg-golden/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl backdrop-blur-md relative z-10 space-y-6">
        <div className="text-center space-y-2.5">
          <div className="p-4 bg-[#B31825]/15 text-[#B31825] rounded-2xl w-fit mx-auto border border-[#B31825]/20 animate-pulse">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h2 className="font-montserrat font-extrabold text-2xl text-white tracking-tight">Sahyadri Admin Portal</h2>
          <p className="text-gray-400 text-xs font-light">Co-operative Security Management Center</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl p-3.5 text-xs flex items-center space-x-2.5">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4 text-white">
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Email ID</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                required
                placeholder="admin@sahyadri.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none transition-all"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Access Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 focus:border-[#B31825] focus:ring-2 focus:ring-[#B31825]/20 rounded-xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 outline-none transition-all"
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#B31825] hover:bg-[#8a0f19] text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Verifying Ledger Access...</span>
                </>
              ) : (
                <span>Authenticate Access</span>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-8 text-center text-[10px] text-gray-600">
        Authorized general admin credential: <span className="text-gray-500 font-mono">admin@sahyadri.com / admin123</span>
      </div>
    </div>
  );
}
